import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { useSeo } from "@/lib/seo";
import { useLang, lp } from "@/lib/i18n";
import { useCart, euro } from "@/lib/cart";

const T = {
  fr: {
    seoTitle: "Panier — Maison Partita",
    seoDesc: "Ton panier. Commande sans paiement en ligne : la maison revient vers toi par e-mail.",
    kicker: "Panier", title: "Ta sélection",
    empty: "Ton panier est vide.", browse: "Découvrir les collections",
    ref: "Référence", qty: "Quantité", unit: "Prix unitaire", sub: "Sous-total", remove: "Retirer",
    total: "Total indicatif", shipping: "Livraison offerte dès 150 €. Des échantillons accompagnent chaque envoi.",
    howTitle: "Comment se passe la commande",
    how: [
      "Tu nous envoies ton panier avec tes coordonnées.",
      "La maison confirme la disponibilité des références sous 48 h ouvrées.",
      "Tu reçois par e-mail les modalités de règlement, puis l'envoi part sous pli numéroté.",
    ],
    noPay: "Aucun paiement n'est demandé sur le site.",
    formTitle: "Tes coordonnées",
    name: "Nom et prénom", email: "Adresse e-mail", phone: "Téléphone",
    address: "Adresse", zip: "Code postal", city: "Ville", country: "Pays",
    message: "Un mot pour la maison (facultatif)",
    consent: "J'accepte que ces informations soient utilisées pour traiter ma commande.",
    send: "Envoyer ma commande", sending: "Envoi…",
    doneKicker: "Commande envoyée",
    doneTitle: "Merci, ta commande nous est parvenue.",
    doneNumber: "Numéro de commande",
    doneBody: "Un récapitulatif vient d'être adressé à ton e-mail. La maison confirme la disponibilité et t'écrit sous 48 h ouvrées avec les modalités de règlement et d'envoi.",
    back: "Retour à l'accueil",
    clear: "Vider le panier",
  },
  en: {
    seoTitle: "Cart — Maison Partita",
    seoDesc: "Your cart. No online payment: the house gets back to you by e-mail.",
    kicker: "Cart", title: "Your selection",
    empty: "Your cart is empty.", browse: "Discover the collections",
    ref: "Reference", qty: "Quantity", unit: "Unit price", sub: "Subtotal", remove: "Remove",
    total: "Indicative total", shipping: "Complimentary delivery from €150. Samples accompany every shipment.",
    howTitle: "How ordering works",
    how: [
      "You send us your cart with your details.",
      "The house confirms the availability of the references within 48 working hours.",
      "You receive the payment details by e-mail, then the parcel leaves under numbered cover.",
    ],
    noPay: "No payment is requested on the site.",
    formTitle: "Your details",
    name: "Full name", email: "E-mail address", phone: "Phone",
    address: "Address", zip: "Postal code", city: "City", country: "Country",
    message: "A word for the house (optional)",
    consent: "I agree that this information is used to process my order.",
    send: "Send my order", sending: "Sending…",
    doneKicker: "Order sent",
    doneTitle: "Thank you, your order has reached us.",
    doneNumber: "Order number",
    doneBody: "A summary has just been sent to your e-mail. The house confirms availability and writes to you within 48 working hours with payment and shipping details.",
    back: "Back to home",
    clear: "Empty the cart",
  },
};

const EMPTY_FORM = { name: "", email: "", phone: "", address: "", zip: "", city: "", country: "France", message: "", consent: false };

export default function Panier() {
  const lang = useLang();
  const t = T[lang];
  useSeo(t.seoTitle, t.seoDesc);
  const { items, total, setQty, remove, clear } = useCart();
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/commande", {
        ...form,
        lang,
        items: items.map((i) => ({ reference: i.reference, label: i.label, collection: i.collection, price: i.price, qty: i.qty })),
      });
      setDone(data);
      clear();
      setForm(EMPTY_FORM);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section className="max-w-2xl mx-auto px-5 sm:px-10 pt-28 pb-32" data-testid="order-done">
        <Reveal>
          <p className="text-[13px] tracking-[0.34em] uppercase text-zinc-500">{t.doneKicker}</p>
          <h1 className="mt-6 text-4xl sm:text-5xl tracking-[0.08em] text-zinc-50">{t.doneTitle}</h1>
          <p className="mt-10 text-[13px] tracking-[0.24em] uppercase text-zinc-500">{t.doneNumber}</p>
          <p className="mt-2 text-2xl" style={{ color: "#C9A86A" }} data-testid="order-number">{done.number}</p>
          <p className="mt-8 text-[17px] leading-[1.85] text-zinc-300">{t.doneBody}</p>
          <Link to={lp(lang, "/")} className="val-btn mt-12" data-testid="order-back">{t.back}</Link>
        </Reveal>
      </section>
    );
  }

  return (
    <section className="max-w-[1100px] mx-auto px-5 sm:px-10 pt-24 pb-32">
      <Reveal>
        <p className="text-[13px] tracking-[0.34em] uppercase text-zinc-500">{t.kicker}</p>
        <h1 className="mt-6 text-4xl sm:text-5xl tracking-[0.08em] text-zinc-50">{t.title}</h1>
      </Reveal>

      {!items.length ? (
        <Reveal>
          <div className="mt-16 border-t border-zinc-900 pt-12" data-testid="cart-empty">
            <p className="text-[19px] text-zinc-400">{t.empty}</p>
            <Link to={lp(lang, "/crisopea")} className="val-btn mt-10" data-testid="cart-browse">{t.browse}</Link>
          </div>
        </Reveal>
      ) : (
        <div className="mt-14 grid gap-16 lg:grid-cols-[1.2fr_1fr]">
          <Reveal>
            <ul className="divide-y divide-zinc-900 border-t border-b border-zinc-900" data-testid="cart-items">
              {items.map((i) => (
                <li key={i.reference} className="py-7 grid gap-4 sm:grid-cols-[1fr_auto_auto] sm:items-center" data-testid={`cart-item-${i.reference}`}>
                  <div>
                    <p className="text-[12px] tracking-[0.24em] uppercase text-zinc-500">{i.collection}</p>
                    {i.url
                      ? <Link to={lp(lang, i.url)} className="val-link text-[19px] tracking-[0.08em] uppercase text-zinc-100">{i.label}</Link>
                      : <p className="text-[19px] tracking-[0.08em] uppercase text-zinc-100">{i.label}</p>}
                    <p className="mt-1 text-[14px] text-zinc-500">{t.unit} : {euro(i.price)}</p>
                  </div>
                  <div className="inline-flex items-center border border-zinc-700 text-zinc-200 justify-self-start" aria-label={t.qty}>
                    <button type="button" onClick={() => setQty(i.reference, i.qty - 1)} className="w-10 h-10" aria-label="−">−</button>
                    <span className="w-8 text-center tabular-nums">{i.qty}</span>
                    <button type="button" onClick={() => setQty(i.reference, i.qty + 1)} className="w-10 h-10" aria-label="+">+</button>
                  </div>
                  <div className="text-right">
                    <p className="text-[19px] text-zinc-100 tabular-nums">{euro(i.qty * i.price)}</p>
                    <button type="button" onClick={() => remove(i.reference)} className="val-link text-[12px] tracking-[0.2em] uppercase mt-1" data-testid="cart-remove">{t.remove}</button>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex items-baseline justify-between">
              <p className="text-[13px] tracking-[0.24em] uppercase text-zinc-500">{t.total}</p>
              <p className="text-3xl tabular-nums" style={{ color: "#C9A86A" }} data-testid="cart-total">{euro(total)}</p>
            </div>
            <p className="mt-3 text-[14px] text-zinc-500">{t.shipping}</p>
            <button type="button" onClick={clear} className="val-link text-[12px] tracking-[0.2em] uppercase mt-6" data-testid="cart-clear">{t.clear}</button>

            <div className="mt-14 border-t border-zinc-900 pt-10">
              <p className="text-[13px] tracking-[0.24em] uppercase text-zinc-500">{t.howTitle}</p>
              <ol className="mt-6 space-y-4">
                {t.how.map((s, i) => (
                  <li key={i} className="flex gap-5 text-zinc-300 leading-relaxed">
                    <span className="shrink-0 tabular-nums" style={{ color: "#C9A86A" }}>{["I", "II", "III"][i]}</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
              <p className="mt-6 text-[14px] text-zinc-500">{t.noPay}</p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="space-y-9" data-testid="order-form">
              <p className="text-[13px] tracking-[0.24em] uppercase text-zinc-500">{t.formTitle}</p>
              <input required value={form.name} onChange={set("name")} placeholder={t.name} autoComplete="name" className="val-input" data-testid="order-name" />
              <input type="email" required value={form.email} onChange={set("email")} placeholder={t.email} autoComplete="email" className="val-input" data-testid="order-email" />
              <input type="tel" required value={form.phone} onChange={set("phone")} placeholder={t.phone} autoComplete="tel" className="val-input" data-testid="order-phone" />
              <input required value={form.address} onChange={set("address")} placeholder={t.address} autoComplete="street-address" className="val-input" data-testid="order-address" />
              <div className="grid grid-cols-[1fr_2fr] gap-6">
                <input required value={form.zip} onChange={set("zip")} placeholder={t.zip} autoComplete="postal-code" className="val-input" data-testid="order-zip" />
                <input required value={form.city} onChange={set("city")} placeholder={t.city} autoComplete="address-level2" className="val-input" data-testid="order-city" />
              </div>
              <input required value={form.country} onChange={set("country")} placeholder={t.country} autoComplete="country-name" className="val-input" data-testid="order-country" />
              <textarea rows={3} value={form.message} onChange={set("message")} placeholder={t.message} className="val-input resize-none" data-testid="order-message" />
              <label className="flex items-start gap-4 text-[14px] leading-relaxed text-zinc-400 cursor-pointer">
                <input type="checkbox" required checked={form.consent} onChange={set("consent")} className="mt-1 accent-[#C9A86A]" data-testid="order-consent" />
                <span>{t.consent}</span>
              </label>
              <button type="submit" disabled={busy || !items.length} className="val-btn w-full sm:w-auto" data-testid="order-submit">
                {busy ? t.sending : t.send}
              </button>
            </form>
          </Reveal>
        </div>
      )}
    </section>
  );
}
